import { AxiosError } from 'axios';

export const REGISTRATION_NOT_ALLOWED_CODE = 'REGISTRATION_NOT_ALLOWED';

export const REGISTRATION_BLOCKED_MESSAGE =
  'Sign up is currently invite-only for our early access cohort. Please use the email address you were invited with. If you believe this is a mistake, contact support.';

const REGISTRATION_BLOCKED_QUERY_KEY = 'registrationBlocked';

const isBrowser = () => typeof window !== 'undefined';

const readRegistrationBlockedCodeFromParams = (params: URLSearchParams): string | null => {
  const candidates = [
    params.get('code'),
    params.get('errorCode'),
    params.get('error'),
  ];

  for (const candidate of candidates) {
    const normalized = candidate?.trim();
    if (normalized === REGISTRATION_NOT_ALLOWED_CODE) {
      return normalized;
    }
  }

  return null;
};

export const readRegistrationBlockedFromUrl = (): boolean => {
  if (!isBrowser()) {
    return false;
  }

  const { search, hash } = window.location;
  const searchParams = new URLSearchParams(search);
  if (searchParams.get(REGISTRATION_BLOCKED_QUERY_KEY) === '1') {
    return true;
  }
  if (readRegistrationBlockedCodeFromParams(searchParams)) {
    return true;
  }

  if (!hash) {
    return false;
  }

  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  if (hashParams.get(REGISTRATION_BLOCKED_QUERY_KEY) === '1') {
    return true;
  }

  return Boolean(readRegistrationBlockedCodeFromParams(hashParams));
};

export const clearRegistrationBlockedFromUrl = () => {
  if (!isBrowser()) {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete(REGISTRATION_BLOCKED_QUERY_KEY);
  url.searchParams.delete('code');
  url.searchParams.delete('errorCode');
  url.searchParams.delete('error');

  if (url.hash) {
    const hashParams = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);
    hashParams.delete(REGISTRATION_BLOCKED_QUERY_KEY);
    hashParams.delete('code');
    hashParams.delete('errorCode');
    hashParams.delete('error');
    const nextHash = hashParams.toString();
    url.hash = nextHash ? `#${nextHash}` : '';
  }

  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
};

export const isRegistrationNotAllowedError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  if (error.response?.status !== 403) {
    return false;
  }

  const data = error.response.data;
  if (!data || typeof data !== 'object') {
    return false;
  }

  const code = (data as Record<string, unknown>).code;
  return code === REGISTRATION_NOT_ALLOWED_CODE;
};
